import { useState } from 'react';
import { Upload, X, Check, AlertCircle, Grid3x3, List, Trash2, Info, RefreshCw } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import config from '@/config';

const VALIDATION_RULES = {
  minWidth: 100,
  minHeight: 100,
  // 4096 is a GPU limit, not a cosmetic one. Upscale 2x doubles the source
  // before anything else runs, so a 5500px frame becomes ~52 megapixels and
  // the upscaler dies with CUDA out of memory - which strands the frame at
  // Image Resize forever instead of rejecting it at the door. 4096 doubles to
  // ~36MP, which fits. Oversized uploads are downscaled to this in the browser
  // (see downscaleToLimits) rather than refused.
  maxWidth: 4096,
  maxHeight: 4096,
  minSize: 1024,
  maxSize: 64 * 1024 * 1024,
  allowedFormats: ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'],
  allowedExtensions: ['.jpg', '.jpeg', '.png', '.gif', '.webp']
};

// Canvas can only re-encode these. A GIF is left alone rather than quietly
// re-encoded: the file NAME becomes the frame identity downstream, so the name
// and the bytes must keep agreeing about the format.
const RESIZABLE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

const loadImageElement = (file) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Unable to decode image'));
    };
    img.src = objectUrl;
  });

const canvasToBlob = (canvas, type, quality) =>
  new Promise((resolve) => canvas.toBlob(resolve, type, quality));

/**
 * Shrink an over-sized upload to fit the dimension limits, in the browser,
 * before it is validated or sent.
 *
 * Returns null when nothing needs doing, so an image already inside the limits
 * is passed through as the original File and never re-encoded.
 */
const downscaleToLimits = async (file) => {
  if (!RESIZABLE_TYPES.includes(file.type)) return null;

  const img = await loadImageElement(file);
  const width = img.naturalWidth;
  const height = img.naturalHeight;

  const ratio = Math.min(
    VALIDATION_RULES.maxWidth / width,
    VALIDATION_RULES.maxHeight / height,
    1
  );
  if (ratio >= 1) return null;

  const targetWidth = Math.max(1, Math.round(width * ratio));
  const targetHeight = Math.max(1, Math.round(height * ratio));

  // Halve repeatedly before the final draw. One big reduction samples too few
  // source pixels and leaves edges aliased, and edges are exactly what lens
  // recognition and segmentation key on later in the pipeline.
  let source = img;
  let stepWidth = width;
  let stepHeight = height;
  while (stepWidth >= targetWidth * 2 && stepHeight >= targetHeight * 2) {
    stepWidth = Math.max(targetWidth, Math.round(stepWidth / 2));
    stepHeight = Math.max(targetHeight, Math.round(stepHeight / 2));
    const step = document.createElement('canvas');
    step.width = stepWidth;
    step.height = stepHeight;
    const stepCtx = step.getContext('2d');
    stepCtx.imageSmoothingEnabled = true;
    stepCtx.imageSmoothingQuality = 'high';
    stepCtx.drawImage(source, 0, 0, stepWidth, stepHeight);
    source = step;
  }

  const canvas = document.createElement('canvas');
  canvas.width = targetWidth;
  canvas.height = targetHeight;
  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  // JPEG carries no alpha channel, so any transparent pixel would be written
  // out as black - the precise failure the ingest's white-matte guard exists
  // to undo. Lay white down first so a stray alpha channel cannot reach it.
  const isJpeg = file.type === 'image/jpeg' || file.type === 'image/jpg';
  if (isJpeg) {
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, targetWidth, targetHeight);
  }
  ctx.drawImage(source, 0, 0, targetWidth, targetHeight);

  // Keep the original type: PNG and WebP keep their alpha, which the ingest
  // then composites onto white itself.
  const blob = await canvasToBlob(canvas, isJpeg ? 'image/jpeg' : file.type, isJpeg ? 0.95 : undefined);
  if (!blob) return null;

  // The name is carried over untouched - frames are looked up by it.
  return {
    file: new File([blob], file.name, { type: blob.type, lastModified: file.lastModified }),
    from: { width, height, size: file.size },
    to: { width: targetWidth, height: targetHeight, size: blob.size }
  };
};

function ImageBatchUploader() {
  const { authenticatedFetch, user } = useAuth();
  
  const [viewMode, setViewMode] = useState('grid');
  const [files, setFiles] = useState([]);
  const [uploadStatus, setUploadStatus] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const [showInfo, setShowInfo] = useState(false);

  const validateImageDimensions = (file) => {
    return new Promise((resolve) => {
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);
      
      img.onload = () => {
        const width = img.naturalWidth;
        const height = img.naturalHeight;
        URL.revokeObjectURL(objectUrl);
        
        const errors = [];
        
        if (width < VALIDATION_RULES.minWidth) {
          errors.push(`Width ${width}px is below minimum ${VALIDATION_RULES.minWidth}px`);
        }
        if (height < VALIDATION_RULES.minHeight) {
          errors.push(`Height ${height}px is below minimum ${VALIDATION_RULES.minHeight}px`);
        }
        if (width > VALIDATION_RULES.maxWidth) {
          errors.push(`Width ${width}px exceeds maximum ${VALIDATION_RULES.maxWidth}px`);
        }
        if (height > VALIDATION_RULES.maxHeight) {
          errors.push(`Height ${height}px exceeds maximum ${VALIDATION_RULES.maxHeight}px`);
        }
        
        resolve({ valid: errors.length === 0, errors, width, height });
      };
      
      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        resolve({ valid: false, errors: ['Unable to read image dimensions'], width: 0, height: 0 });
      };
      
      img.src = objectUrl;
    });
  };

  const validateFile = async (file) => {
    const errors = [];
    
    if (!VALIDATION_RULES.allowedFormats.includes(file.type)) {
      errors.push(`Invalid format: ${file.type}`);
    }
    
    if (file.size < VALIDATION_RULES.minSize) {
      errors.push(`File too small: ${formatFileSize(file.size)}`);
    }
    if (file.size > VALIDATION_RULES.maxSize) {
      errors.push(`File too large: ${formatFileSize(file.size)}`);
    }
    
    const dimensionCheck = await validateImageDimensions(file);
    if (!dimensionCheck.valid) {
      errors.push(...dimensionCheck.errors);
    }
    
    return {
      valid: errors.length === 0,
      errors,
      dimensions: dimensionCheck.valid ? { width: dimensionCheck.width, height: dimensionCheck.height } : null
    };
  };

  const handleFileSelect = async (e) => {
    const selectedFiles = Array.from(e.target.files);
    const imageFiles = selectedFiles.filter(file => file.type.startsWith('image/'));
    
    const filesWithValidation = await Promise.all(
      imageFiles.map(async (original) => {
        // Shrink first, then validate what will actually be sent. An image that
        // was only over on dimensions now arrives valid instead of being
        // rejected at the door.
        let file = original;
        let resized = null;
        try {
          const shrunk = await downscaleToLimits(original);
          if (shrunk) {
            file = shrunk.file;
            resized = { from: shrunk.from, to: shrunk.to };
          }
        } catch (error) {
          // Fall through with the original; validation reports the real reason.
          console.error(`Could not downscale ${original.name}:`, error);
        }

        const validation = await validateFile(file);
        return {
          file,
          id: Math.random().toString(36).substr(2, 9),
          preview: URL.createObjectURL(file),
          name: file.name,
          size: file.size,
          validation,
          resized
        };
      })
    );
    
    setFiles(prev => [...prev, ...filesWithValidation]);
  };

  const removeFile = (id) => {
    setFiles(prev => {
      const fileToRemove = prev.find(f => f.id === id);
      if (fileToRemove?.preview) {
        URL.revokeObjectURL(fileToRemove.preview);
      }
      return prev.filter(f => f.id !== id);
    });
    setUploadStatus(prev => {
      const newStatus = { ...prev };
      delete newStatus[id];
      return newStatus;
    });
  };

  const clearCompleted = () => {
    setFiles(prev => {
      const toRemove = prev.filter(f => uploadStatus[f.id]?.status === 'success');
      toRemove.forEach(f => f.preview && URL.revokeObjectURL(f.preview));
      return prev.filter(f => uploadStatus[f.id]?.status !== 'success');
    });
    setUploadStatus(prev => {
      const newStatus = { ...prev };
      Object.keys(newStatus).forEach(id => {
        if (newStatus[id].status === 'success') {
          delete newStatus[id];
        }
      });
      return newStatus;
    });
  };

  const uploadToBackend = async (fileData) => {
    const formData = new FormData();
    formData.append('image', fileData.file, fileData.name);
    
    try {
      const response = await authenticatedFetch(
        `${config.BACKEND_API_ADDRESS}/ingest/images`,
        {
          method: 'POST',
          body: formData
        }
      );
      
      if (!response.ok) {
        let errorMessage = `Upload failed: ${response.status}`;
        try {
          const errorData = await response.json();
          console.error('Backend error response:', errorData);
          errorMessage = errorData.detail || JSON.stringify(errorData);
        } catch (e) {
          const errorText = await response.text();
          console.error('Backend error text:', errorText);
          errorMessage = errorText || errorMessage;
        }
        throw new Error(errorMessage);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Upload error details:', error);
      throw error;
    }
  };

  const handleUpload = async () => {
    const validFiles = files.filter(f => f.validation.valid && f.file);
    
    if (validFiles.length === 0) return;
    
    setIsUploading(true);
    
    for (const fileData of validFiles) {
      if (uploadStatus[fileData.id]?.status === 'success') continue;
      
      try {
        setUploadStatus(prev => ({
          ...prev,
          [fileData.id]: { status: 'uploading', progress: 0 }
        }));
        
        const result = await uploadToBackend(fileData);
        
        setUploadStatus(prev => ({
          ...prev,
          [fileData.id]: { 
            status: 'success', 
            progress: 100, 
            result,
            backendStatus: result.status,
            message: result.message
          }
        }));
      } catch (error) {
        console.error(`Error uploading ${fileData.name}:`, error);
        setUploadStatus(prev => ({
          ...prev,
          [fileData.id]: { 
            status: 'error', 
            progress: 0,
            error: error.message 
          }
        }));
      }
    }
    
    setIsUploading(false);
  };

  const retryFailedUploads = async () => {
    const failedFiles = files.filter(f => uploadStatus[f.id]?.status === 'error');
    
    if (failedFiles.length === 0) return;
    
    setIsUploading(true);
    
    for (const fileData of failedFiles) {
      try {
        setUploadStatus(prev => ({
          ...prev,
          [fileData.id]: { status: 'uploading', progress: 0 }
        }));
        
        const result = await uploadToBackend(fileData);
        
        setUploadStatus(prev => ({
          ...prev,
          [fileData.id]: { 
            status: 'success', 
            progress: 100, 
            result,
            backendStatus: result.status,
            message: result.message
          }
        }));
      } catch (error) {
        console.error(`Error uploading ${fileData.name}:`, error);
        setUploadStatus(prev => ({
          ...prev,
          [fileData.id]: { 
            status: 'error', 
            progress: 0,
            error: error.message 
          }
        }));
      }
    }
    
    setIsUploading(false);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const validFiles = files.filter(f => f.validation.valid);
  const invalidFiles = files.filter(f => !f.validation.valid);
  
  const uploadedValidFiles = validFiles.filter(f => uploadStatus[f.id]?.status === 'success');
  const allValidUploaded = validFiles.length > 0 && uploadedValidFiles.length === validFiles.length;

  const uploadStats = {
    total: validFiles.length,
    success: Object.values(uploadStatus).filter(s => s.status === 'success').length,
    error: Object.values(uploadStatus).filter(s => s.status === 'error').length,
    uploading: Object.values(uploadStatus).filter(s => s.status === 'uploading').length,
    invalid: invalidFiles.length,
    created: Object.values(uploadStatus).filter(s => s.backendStatus === 'created').length,
    duplicate: Object.values(uploadStatus).filter(s => s.backendStatus === 'duplicate').length,
    upgraded: Object.values(uploadStatus).filter(s => s.backendStatus === 'upgraded').length,
    linked: Object.values(uploadStatus).filter(s => s.backendStatus === 'linked').length
  };

  const FileCard = ({ fileData, isInvalid }) => {
    const status = uploadStatus[fileData.id];
    
    return (
      <div 
        className={`relative rounded-lg border transition-all ${
          isInvalid
            ? 'bg-red-50 border-red-200'
            : status?.status === 'uploading'
            ? 'bg-gradient-to-r from-blue-50 via-blue-100 to-blue-50 border-blue-300 animate-pulse'
            : status?.status === 'success'
            ? status.backendStatus === 'duplicate' || status.backendStatus === 'rejected'
              ? 'bg-yellow-50 border-yellow-200'
              : 'bg-green-50 border-green-200'
            : status?.status === 'error'
            ? 'bg-red-50 border-red-200'
            : 'bg-white border-gray-200 hover:border-blue-300 hover:shadow-md'
        }`}
      >
        <div className="aspect-square relative overflow-hidden rounded-t-lg bg-gray-100">
          {fileData.preview ? (
            <img
              src={fileData.preview}
              alt={fileData.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-400">
              <Upload className="w-12 h-12" />
            </div>
          )}
          
          <div className="absolute top-1.5 right-1.5">
            {!status && !isUploading && !isInvalid && (
              <button
                onClick={() => removeFile(fileData.id)}
                className="p-1 bg-white/90 backdrop-blur-sm text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-full shadow-sm transition-colors"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
            
            {status?.status === 'uploading' && (
              <div className="w-6 h-6 bg-white rounded-full flex items-center justify-center shadow-sm">
                <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
              </div>
            )}
            
            {status?.status === 'success' && (
              <div className={`w-6 h-6 rounded-full flex items-center justify-center shadow-sm ${
                status.backendStatus === 'duplicate' || status.backendStatus === 'rejected'
                  ? 'bg-yellow-500'
                  : 'bg-green-500'
              }`}>
                <Check className="w-4 h-4 text-white stroke-[3]" />
              </div>
            )}
            
            {status?.status === 'error' && (
              <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center shadow-sm">
                <X className="w-4 h-4 text-white stroke-[3]" />
              </div>
            )}
            
            {isInvalid && !status && (
              <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center shadow-sm">
                <AlertCircle className="w-4 h-4 text-white" />
              </div>
            )}
          </div>
        </div>
        
        <div className="p-3">
          <div className="font-medium text-xs text-gray-800 truncate mb-1" title={fileData.name}>
            {fileData.name}
          </div>
          <div className="text-xs text-gray-500">
            {formatFileSize(fileData.size)}
          </div>
          
          {status?.backendStatus && status.status === 'success' && (
            <div className={`text-xs mt-1 font-medium ${
              status.backendStatus === 'duplicate' || status.backendStatus === 'rejected'
                ? 'text-yellow-700'
                : 'text-green-700'
            }`}>
              {status.backendStatus === 'created' && '✓ New'}
              {status.backendStatus === 'duplicate' && '⚠ Duplicate'}
              {status.backendStatus === 'upgraded' && '↑ Upgraded'}
              {status.backendStatus === 'linked' && '🔗 Linked'}
              {status.backendStatus === 'rejected' && '✗ Rejected'}
            </div>
          )}
          
          {fileData.resized && (
            <div
              className="text-xs text-amber-700 mt-1 truncate"
              title={`Resized in browser from ${fileData.resized.from.width}×${fileData.resized.from.height}px to ${fileData.resized.to.width}×${fileData.resized.to.height}px`}
            >
              ↓ Resized to {fileData.resized.to.width}×{fileData.resized.to.height}px
            </div>
          )}

          {isInvalid && (
            <div className="text-xs text-red-600 mt-1">
              {fileData.validation.errors.map((error, idx) => (
                <div key={idx} className="truncate" title={error}>• {error}</div>
              ))}
            </div>
          )}
          
          {status?.error && (
            <div className="text-xs text-red-600 mt-1 truncate" title={status.error}>
              Failed
            </div>
          )}
        </div>
      </div>
    );
  };

  const FileRow = ({ fileData, isInvalid }) => {
    const status = uploadStatus[fileData.id];
    
    return (
      <div 
        className={`flex items-center gap-4 p-3 rounded-lg border transition-colors ${
          isInvalid
            ? 'bg-red-50 border-red-200'
            : status?.status === 'uploading'
            ? 'bg-gradient-to-r from-blue-50 via-blue-100 to-blue-50 border-blue-300'
            : status?.status === 'success'
            ? status.backendStatus === 'duplicate' || status.backendStatus === 'rejected'
              ? 'bg-yellow-50 border-yellow-200'
              : 'bg-green-50 border-green-200'
            : 'bg-white border-gray-200 hover:bg-gray-50'
        }`}
      >
        <div className="w-16 h-16 rounded overflow-hidden bg-gray-200 flex-shrink-0">
          {fileData.preview ? (
            <img
              src={fileData.preview}
              alt={fileData.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-400">
              <Upload className="w-8 h-8" />
            </div>
          )}
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="font-medium text-gray-800 truncate">
            {fileData.name}
          </div>
          <div className="text-sm text-gray-500">
            {formatFileSize(fileData.size)}
            {fileData.validation.dimensions && (
              <span className="ml-2">
                • {fileData.validation.dimensions.width}×{fileData.validation.dimensions.height}px
              </span>
            )}
          </div>
          {status?.message && status.status === 'success' && (
            <div className="text-xs text-gray-600 mt-1">
              {status.message}
            </div>
          )}
          {fileData.resized && (
            <div className="text-xs text-amber-700 mt-1">
              ↓ Resized in browser: {fileData.resized.from.width}×{fileData.resized.from.height}px
              {' → '}{fileData.resized.to.width}×{fileData.resized.to.height}px
              {' ('}{formatFileSize(fileData.resized.from.size)}
              {' → '}{formatFileSize(fileData.resized.to.size)}{')'}
            </div>
          )}

          {isInvalid && (
            <div className="text-xs text-red-600 mt-1 space-y-0.5">
              {fileData.validation.errors.map((error, idx) => (
                <div key={idx}>• {error}</div>
              ))}
            </div>
          )}
          {status?.error && (
            <div className="text-xs text-red-500 mt-1">
              {status.error}
            </div>
          )}
        </div>
        
        <div className="flex-shrink-0">
          {!status && !isUploading && (
            <button
              onClick={() => removeFile(fileData.id)}
              className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          )}
          
          {status?.status === 'uploading' && (
            <div className="w-8 h-8 border-3 border-blue-500 border-t-transparent rounded-full animate-spin" />
          )}
          
          {status?.status === 'success' && (
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
              status.backendStatus === 'duplicate' || status.backendStatus === 'rejected'
                ? 'bg-yellow-500'
                : 'bg-green-500'
            }`}>
              <Check className="w-5 h-5 text-white stroke-[3]" />
            </div>
          )}
          
          {status?.status === 'error' && (
            <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center">
              <X className="w-5 h-5 text-white stroke-[3]" />
            </div>
          )}
          
          {isInvalid && !status && (
            <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center">
              <AlertCircle className="w-5 h-5 text-white" />
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-full mx-auto">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-1">Batch Image Uploader</h2>
            <p className="text-gray-600 text-sm">Upload multiple images with validation and deduplication</p>
          </div>
          
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded transition-colors ${
                    viewMode === 'grid' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-600 hover:text-gray-800'
                  }`}
                  title="Grid View"
                >
                  <Grid3x3 className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded transition-colors ${
                    viewMode === 'list' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-600 hover:text-gray-800'
                  }`}
                  title="List View"
                >
                  <List className="w-5 h-5" />
                </button>
              </div>
              <button
                onClick={() => setShowInfo(!showInfo)}
                className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                title="Validation Info"
              >
                <Info className="w-5 h-5" />
              </button>
            </div>
          </div>

          {showInfo && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h3 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                <Info className="w-5 h-5" />
                Validation Rules
              </h3>
              <div className="space-y-3 text-sm">
                <div>
                  <p className="font-medium text-blue-800 mb-1">Image Dimensions:</p>
                  <ul className="list-disc list-inside text-blue-700 space-y-0.5 ml-2">
                    <li>Minimum: {VALIDATION_RULES.minWidth}×{VALIDATION_RULES.minHeight}px</li>
                    <li>Maximum: {VALIDATION_RULES.maxWidth}×{VALIDATION_RULES.maxHeight}px</li>
                    <li>
                      Anything larger is downscaled in your browser to fit, keeping
                      its aspect ratio, filename and format. Only the resized copy
                      is uploaded.
                    </li>
                  </ul>
                </div>
                <div>
                  <p className="font-medium text-blue-800 mb-1">File Size:</p>
                  <ul className="list-disc list-inside text-blue-700 space-y-0.5 ml-2">
                    <li>Minimum: {formatFileSize(VALIDATION_RULES.minSize)}</li>
                    <li>Maximum: {formatFileSize(VALIDATION_RULES.maxSize)}</li>
                  </ul>
                </div>
                <div>
                  <p className="font-medium text-blue-800 mb-1">Allowed Formats:</p>
                  <ul className="list-disc list-inside text-blue-700 space-y-0.5 ml-2">
                    {VALIDATION_RULES.allowedExtensions.map((ext, idx) => (
                      <li key={idx}>{ext.toUpperCase()}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}
          
          <div className="mb-6">
            <label className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors">
              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                <Upload className="w-12 h-12 mb-3 text-gray-400" />
                <p className="mb-2 text-sm text-gray-500">
                  <span className="font-semibold">Click to upload</span> or drag and drop
                </p>
                <p className="text-xs text-gray-500">
                  {VALIDATION_RULES.allowedExtensions.join(', ').toUpperCase()} • Max {formatFileSize(VALIDATION_RULES.maxSize)}
                </p>
              </div>
              <input
                type="file"
                multiple
                accept={VALIDATION_RULES.allowedFormats.join(',')}
                onChange={handleFileSelect}
                className="hidden"
                disabled={isUploading}
              />
            </label>
          </div>

          {files.length > 0 && (
            <>
              <div className="mb-4 flex flex-wrap justify-between items-center gap-2">
                <div className="text-sm text-gray-600 flex flex-wrap gap-x-3 gap-y-1">
                  <span><span className="font-medium">{validFiles.length}</span> valid</span>
                  <span><span className="font-medium text-red-600">{invalidFiles.length}</span> invalid</span>
                  <span><span className="font-medium text-green-600">{uploadStats.created}</span> new</span>
                  <span><span className="font-medium text-yellow-600">{uploadStats.duplicate}</span> duplicate</span>
                  {uploadStats.upgraded > 0 && <span><span className="font-medium text-blue-600">{uploadStats.upgraded}</span> upgraded</span>}
                  {uploadStats.linked > 0 && <span><span className="font-medium text-purple-600">{uploadStats.linked}</span> linked</span>}
                </div>
                <div className="flex gap-2">
                  {uploadStats.error > 0 && !isUploading && (
                    <button
                      onClick={retryFailedUploads}
                      className="text-sm px-3 py-1.5 text-orange-700 hover:text-orange-800 bg-orange-100 hover:bg-orange-200 rounded-lg transition-colors flex items-center gap-1"
                    >
                      <RefreshCw className="w-4 h-4" />
                      Retry Failed
                    </button>
                  )}
                  {uploadStats.success > 0 && !isUploading && (
                    <button
                      onClick={clearCompleted}
                      className="text-sm px-3 py-1.5 text-green-700 hover:text-green-800 bg-green-100 hover:bg-green-200 rounded-lg transition-colors flex items-center gap-1"
                    >
                      <Trash2 className="w-4 h-4" />
                      Clear Completed
                    </button>
                  )}
                  {!isUploading && (
                    <button
                      onClick={() => {
                        files.forEach(f => f.preview && URL.revokeObjectURL(f.preview));
                        setFiles([]);
                        setUploadStatus({});
                      }}
                      className="text-sm px-3 py-1.5 text-red-600 hover:text-red-800 bg-red-50 hover:bg-red-100 rounded-lg transition-colors"
                    >
                      Clear All
                    </button>
                  )}
                </div>
              </div>
            
              <div className={`mb-6 max-h-[600px] overflow-y-auto ${
                viewMode === 'grid' 
                  ? 'grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10 gap-3' 
                  : 'space-y-2'
              }`}>
                {files.map(fileData => {
                  const isInvalid = !fileData.validation.valid;
                  return viewMode === 'grid' ? (
                    <FileCard key={fileData.id} fileData={fileData} isInvalid={isInvalid} />
                  ) : (
                    <FileRow key={fileData.id} fileData={fileData} isInvalid={isInvalid} />
                  );
                })}
              </div>

              {invalidFiles.length > 0 && (
                <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-yellow-800">Validation Issues</p>
                      <p className="text-xs text-yellow-700 mt-1">
                        {invalidFiles.length} file{invalidFiles.length !== 1 ? 's' : ''} failed validation and will not be uploaded.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <button
                onClick={handleUpload}
                disabled={isUploading || allValidUploaded || validFiles.length === 0}
                className={`w-full py-3 px-4 rounded-lg font-semibold transition-colors ${
                  isUploading || allValidUploaded || validFiles.length === 0
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {isUploading
                  ? 'Uploading...'
                  : allValidUploaded
                  ? 'All Valid Images Uploaded Successfully'
                  : validFiles.length === 0
                  ? 'No Valid Images to Upload'
                  : `Upload ${validFiles.length} Valid Image${validFiles.length !== 1 ? 's' : ''}`}
              </button>

              {isUploading && (
                <div className="mt-4">
                  <div className="flex justify-between text-sm text-gray-600 mb-2">
                    <span>Progress</span>
                    <span>
                      {uploadStats.success} / {uploadStats.total}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{
                        width: `${uploadStats.total > 0 ? (uploadStats.success / uploadStats.total) * 100 : 0}%`
                      }}
                    />
                  </div>
                </div>
              )}

              {allValidUploaded && (
                <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-green-800">Upload Complete!</p>
                      <div className="text-xs text-green-700 mt-1 space-y-1">
                        <p>• New images: {uploadStats.created}</p>
                        {uploadStats.duplicate > 0 && <p>• Duplicates: {uploadStats.duplicate}</p>}
                        {uploadStats.upgraded > 0 && <p>• Upgraded: {uploadStats.upgraded}</p>}
                        {uploadStats.linked > 0 && <p>• Linked: {uploadStats.linked}</p>}
                        {uploadStats.error > 0 && <p>• Failed: {uploadStats.error}</p>}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {uploadStats.error > 0 && !isUploading && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-red-800">Some uploads failed</p>
                      <p className="text-xs text-red-700 mt-1">
                        {uploadStats.error} image{uploadStats.error !== 1 ? 's' : ''} failed to upload. Click "Retry Failed" to try again.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default ImageBatchUploader;